import React from "react";
import { SHORTCUT_ACTIONS } from "../../shortcuts/defaultBindings";
import { useShortcutAction } from "../../shortcuts/useShortcutAction";
import { useSelectionStore } from "../../stores/selectionStore";
import { useEqualities, useTendons } from "../../stores/sceneStore";
import { useViewerActions, useViewerStore } from "../../stores/viewerStore";
import {
  CAMERA_VIEWPOINTS,
  fitCameraToEntireScene,
  focusCameraOnSelection,
  moveCameraToDirection,
  resetCameraToHomeOrDefault,
} from "../../runtime/cameraViewActions";
import { pulseCameraViewMenuShortcut } from "../../runtime/cameraViewMenuShortcutPulse";
import { readCameraViewPreferences } from "../../runtime/cameraViewPreferences";
import { setOverlayLayerVisible, useConstraintVisibilityUiSnapshot } from "./visibilityLabels";

/** Registers Shift+B/O/J/V/C/S/I/E/N for scene selection priority (see defaultBindings.js). */
function useViewportScenePriorityShortcuts() {
  const setScenePrioritySelection = useViewerStore(
    (state) => state.actions.setScenePrioritySelection
  );
  const shortcutOptions = React.useMemo(
    () => ({
      preventDefault: true,
      allowInEditable: false,
    }),
    []
  );

  const onBody = React.useCallback(() => {
    setScenePrioritySelection("body", true);
    return true;
  }, [setScenePrioritySelection]);
  const onObject = React.useCallback(() => {
    setScenePrioritySelection("object", true);
    return true;
  }, [setScenePrioritySelection]);
  const onJoint = React.useCallback(() => {
    setScenePrioritySelection("joint", true);
    return true;
  }, [setScenePrioritySelection]);
  const onVisuals = React.useCallback(() => {
    setScenePrioritySelection(["mesh", "visualGeom"], true);
    return true;
  }, [setScenePrioritySelection]);
  const onCollision = React.useCallback(() => {
    setScenePrioritySelection("collision", true);
    return true;
  }, [setScenePrioritySelection]);
  const onSite = React.useCallback(() => {
    setScenePrioritySelection("site", true);
    return true;
  }, [setScenePrioritySelection]);
  const onInertial = React.useCallback(() => {
    setScenePrioritySelection("inertial", true);
    return true;
  }, [setScenePrioritySelection]);
  const onConstraint = React.useCallback(() => {
    setScenePrioritySelection("constraint", true);
    return true;
  }, [setScenePrioritySelection]);
  const onTendon = React.useCallback(() => {
    setScenePrioritySelection("tendon", true);
    return true;
  }, [setScenePrioritySelection]);

  useShortcutAction(SHORTCUT_ACTIONS.VIEWER_PRIORITY_BODY, onBody, shortcutOptions);
  useShortcutAction(SHORTCUT_ACTIONS.VIEWER_PRIORITY_OBJECT, onObject, shortcutOptions);
  useShortcutAction(SHORTCUT_ACTIONS.VIEWER_PRIORITY_JOINT, onJoint, shortcutOptions);
  useShortcutAction(SHORTCUT_ACTIONS.VIEWER_PRIORITY_VISUALS, onVisuals, shortcutOptions);
  useShortcutAction(SHORTCUT_ACTIONS.VIEWER_PRIORITY_COLLISION, onCollision, shortcutOptions);
  useShortcutAction(SHORTCUT_ACTIONS.VIEWER_PRIORITY_SITE, onSite, shortcutOptions);
  useShortcutAction(SHORTCUT_ACTIONS.VIEWER_PRIORITY_INERTIAL, onInertial, shortcutOptions);
  useShortcutAction(SHORTCUT_ACTIONS.VIEWER_PRIORITY_CONSTRAINT, onConstraint, shortcutOptions);
  useShortcutAction(SHORTCUT_ACTIONS.VIEWER_PRIORITY_TENDON, onTendon, shortcutOptions);
}

/** Registers V / C / J / S / I / E / N / A to toggle viewport layer visibility (see defaultBindings.js). */
function useViewportLayerShortcuts() {
  const viewerActions = useViewerActions();
  const equalities = useEqualities();
  const tendons = useTendons();
  const showConstraintMarkers = useViewerStore((state) => state.showConstraintMarkers);
  const equalityMarkerVisibility = useViewerStore((state) => state.equalityMarkerVisibility);
  const tendonMarkerVisibility = useViewerStore((state) => state.tendonMarkerVisibility);
  const constraintVisibilityUi = useConstraintVisibilityUiSnapshot();
  const shortcutOptions = React.useMemo(
    () => ({
      preventDefault: true,
      allowInEditable: false,
    }),
    []
  );

  const onToggleVisual = React.useCallback(() => {
    viewerActions.toggleShowVisual();
    return true;
  }, [viewerActions]);
  const onToggleCollision = React.useCallback(() => {
    viewerActions.toggleShowCollision();
    return true;
  }, [viewerActions]);
  const onToggleJoints = React.useCallback(() => {
    viewerActions.toggleShowJoints();
    return true;
  }, [viewerActions]);
  const onToggleSites = React.useCallback(() => {
    viewerActions.toggleShowSites();
    return true;
  }, [viewerActions]);
  const onToggleInertials = React.useCallback(() => {
    viewerActions.toggleShowInertials();
    return true;
  }, [viewerActions]);
  const toggleOverlayKind = React.useCallback(
    (kind) => {
      const ids =
        kind === "tendon"
          ? Object.values(tendons || {})
              .map((item) => item?.uuid)
              .filter(Boolean)
          : Object.values(equalities || {})
              .map((item) => item?.uuid)
              .filter(Boolean);
      if (ids.length === 0) return true;
      const visibilityMap = kind === "tendon" ? tendonMarkerVisibility : equalityMarkerVisibility;
      const layerVisible =
        kind === "tendon"
          ? constraintVisibilityUi.tendonsVisible !== false
          : constraintVisibilityUi.constraintsVisible !== false;
      const anyVisible =
        showConstraintMarkers &&
        layerVisible &&
        ids.some((id) => (visibilityMap[id] ?? true) !== false);
      const nextVisible = !anyVisible;
      setOverlayLayerVisible(kind, nextVisible, showConstraintMarkers, viewerActions);
      const setVisible =
        kind === "tendon"
          ? viewerActions.setTendonMarkerVisibility
          : viewerActions.setEqualityMarkerVisibility;
      if (nextVisible) ids.forEach((id) => setVisible(id, true));
      return true;
    },
    [
      constraintVisibilityUi.constraintsVisible,
      constraintVisibilityUi.tendonsVisible,
      equalities,
      equalityMarkerVisibility,
      showConstraintMarkers,
      tendonMarkerVisibility,
      tendons,
      viewerActions,
    ]
  );

  const onToggleConstraintMarkers = React.useCallback(() => {
    toggleOverlayKind("equality");
    return true;
  }, [toggleOverlayKind]);
  const onToggleTendonMarkers = React.useCallback(() => {
    toggleOverlayKind("tendon");
    return true;
  }, [toggleOverlayKind]);
  const onToggleActuators = React.useCallback(() => {
    viewerActions.toggleShowActuators();
    return true;
  }, [viewerActions]);

  useShortcutAction(SHORTCUT_ACTIONS.VIEWER_LAYER_TOGGLE_VISUAL, onToggleVisual, shortcutOptions);
  useShortcutAction(
    SHORTCUT_ACTIONS.VIEWER_LAYER_TOGGLE_COLLISION,
    onToggleCollision,
    shortcutOptions
  );
  useShortcutAction(SHORTCUT_ACTIONS.VIEWER_LAYER_TOGGLE_JOINTS, onToggleJoints, shortcutOptions);
  useShortcutAction(SHORTCUT_ACTIONS.VIEWER_LAYER_TOGGLE_SITES, onToggleSites, shortcutOptions);
  useShortcutAction(
    SHORTCUT_ACTIONS.VIEWER_LAYER_TOGGLE_INERTIALS,
    onToggleInertials,
    shortcutOptions
  );
  useShortcutAction(
    SHORTCUT_ACTIONS.VIEWER_LAYER_TOGGLE_CONSTRAINT_MARKERS,
    onToggleConstraintMarkers,
    shortcutOptions
  );
  useShortcutAction(
    SHORTCUT_ACTIONS.VIEWER_LAYER_TOGGLE_TENDON_MARKERS,
    onToggleTendonMarkers,
    shortcutOptions
  );
  useShortcutAction(
    SHORTCUT_ACTIONS.VIEWER_LAYER_TOGGLE_ACTUATORS,
    onToggleActuators,
    shortcutOptions
  );
}

/** Orthographic views, reset, focus, and fit — see defaultBindings.js (`Camera` group). */
function useViewportCameraShortcuts() {
  const shortcutOptions = React.useMemo(
    () => ({
      preventDefault: true,
      allowInEditable: false,
    }),
    []
  );

  const applyView = React.useCallback((viewKey) => {
    const prefs = readCameraViewPreferences();
    const vp = CAMERA_VIEWPOINTS.find((v) => v.key === viewKey);
    if (!vp) return true;
    moveCameraToDirection(vp.direction, vp.up, { motionMode: prefs.motionMode });
    return true;
  }, []);

  const onTop = React.useCallback(() => {
    pulseCameraViewMenuShortcut({ kind: "orthogonal", key: "top" });
    return applyView("top");
  }, [applyView]);
  const onBottom = React.useCallback(() => {
    pulseCameraViewMenuShortcut({ kind: "orthogonal", key: "bottom" });
    return applyView("bottom");
  }, [applyView]);
  const onFront = React.useCallback(() => {
    pulseCameraViewMenuShortcut({ kind: "orthogonal", key: "front" });
    return applyView("front");
  }, [applyView]);
  const onBack = React.useCallback(() => {
    pulseCameraViewMenuShortcut({ kind: "orthogonal", key: "back" });
    return applyView("back");
  }, [applyView]);
  const onLeft = React.useCallback(() => {
    pulseCameraViewMenuShortcut({ kind: "orthogonal", key: "left" });
    return applyView("left");
  }, [applyView]);
  const onRight = React.useCallback(() => {
    pulseCameraViewMenuShortcut({ kind: "orthogonal", key: "right" });
    return applyView("right");
  }, [applyView]);

  const onReset = React.useCallback(() => {
    pulseCameraViewMenuShortcut({ kind: "reset" });
    resetCameraToHomeOrDefault(readCameraViewPreferences());
    return true;
  }, []);

  const onFocusSelection = React.useCallback(() => {
    pulseCameraViewMenuShortcut({ kind: "focus" });
    const prefs = readCameraViewPreferences();
    const s = useSelectionStore.getState();
    focusCameraOnSelection(
      {
        selectedBody: s.selectedBody,
        selectedMesh: s.selectedMesh,
        selectedGeom: s.selectedGeom,
        selectedJoint: s.selectedJoint,
        selectedSite: s.selectedSite,
        selectedInertial: s.selectedInertial,
      },
      prefs
    );
    return true;
  }, []);

  const onFitScene = React.useCallback(() => {
    pulseCameraViewMenuShortcut({ kind: "fit" });
    fitCameraToEntireScene(readCameraViewPreferences());
    return true;
  }, []);

  useShortcutAction(SHORTCUT_ACTIONS.VIEWER_CAMERA_VIEW_TOP, onTop, shortcutOptions);
  useShortcutAction(SHORTCUT_ACTIONS.VIEWER_CAMERA_VIEW_BOTTOM, onBottom, shortcutOptions);
  useShortcutAction(SHORTCUT_ACTIONS.VIEWER_CAMERA_VIEW_FRONT, onFront, shortcutOptions);
  useShortcutAction(SHORTCUT_ACTIONS.VIEWER_CAMERA_VIEW_BACK, onBack, shortcutOptions);
  useShortcutAction(SHORTCUT_ACTIONS.VIEWER_CAMERA_VIEW_LEFT, onLeft, shortcutOptions);
  useShortcutAction(SHORTCUT_ACTIONS.VIEWER_CAMERA_VIEW_RIGHT, onRight, shortcutOptions);
  useShortcutAction(SHORTCUT_ACTIONS.VIEWER_CAMERA_RESET, onReset, shortcutOptions);
  useShortcutAction(
    SHORTCUT_ACTIONS.VIEWER_CAMERA_FOCUS_SELECTION,
    onFocusSelection,
    shortcutOptions
  );
  useShortcutAction(SHORTCUT_ACTIONS.VIEWER_CAMERA_FIT_SCENE, onFitScene, shortcutOptions);
}

export { useViewportCameraShortcuts, useViewportLayerShortcuts, useViewportScenePriorityShortcuts };
